import type { PagedQueryParams } from './IPagedCollection';

export interface IAvailableWorkAction {
  statusId: number;
  statusCode: string;
  label: string;
  icon?: string | null;
}

export const groupWorkActions = (actions: readonly IAvailableWorkAction[]) => {
  const primary = [
    'IN_PROGRESS',
    'INSPECTION_COMPLETE',
    'REVIEW_PENDING',
    'COMPLETED',
  ]
    .map((code) => actions.find((action) => action.statusCode === code))
    .find((action) => action != null);
  const secondary = actions.find(
    (action) => action.statusCode === 'REPORT_REJECTED'
  );
  return {
    primary,
    secondary,
    more: actions.filter(
      (action) => action !== primary && action !== secondary
    ),
  };
};

export interface IWorkStatuses {
  id: number;
  statusId?: number;
  name: string;
  code: string;
  color: string;
  icon: string;
  lockEntity?: boolean;
}

export const findStatusOption = <T extends { id: number; statusId?: number }>(
  statuses: readonly T[] | undefined,
  statusId: number | string | null | undefined
): T | undefined => {
  const id =
    typeof statusId === 'string' ? Number.parseInt(statusId, 10) : statusId;
  if (id == null || Number.isNaN(Number(id))) return undefined;
  return (
    statuses?.find((status) => status.id === Number(id)) ??
    statuses?.find((status) => status.statusId === Number(id))
  );
};

export const isEntityLocked = (
  statuses:
    | { id: number; statusId?: number; lockEntity?: boolean }[]
    | undefined,
  statusId: number | string | null | undefined
): boolean => Boolean(findStatusOption(statuses, statusId)?.lockEntity);

export interface IWorkStatusHistoryItem {
  fromStatusId?: number;
  fromStatus?: string;
  toStatusId?: number;
  toStatus?: string;
  dateTime?: string;
  changedByUserId?: number;
  changedBy?: string;
}

export interface IWorkStatusHistoryNode {
  key: string;
  status: string;
  statusId?: number;
  dateTime?: string;
  changedBy?: string;
}

export interface ITimesheetEntry {
  id: number;
  note: string;
  startAt: string;
  endAt: string;
}

export type NotifyAudience = 'Managers';

export type CommentVisibility =
  | 'private'
  | 'managers'
  | 'client'
  | 'contractor';

export interface IWorkComment {
  id: number;
  content: string;
  createByUserName?: string;
  createdAt?: Date;
  createdAtFormatted?: string;
  modifiedByUserName?: string;
  modifiedAt?: Date;
  modifiedAtFormatted?: string;
  notifyAudience?: NotifyAudience;
  /** Either 'private' or a comma-separated list of role tokens (see
   *  CommentVisibility). Stored/transported as a string. */
  visibility?: string;
}

export interface IWorkImage {
  id: number;
  createByUserName?: string;
  createdAt?: Date;
  createdAtFormatted?: string;
  imageThumbnail: string;
  imageUrl: string;
  type: string;
  caption?: string | null;
}

export interface IWorkImageContent {
  imageContent: string;
  location: { latitude: number; longitude: number } | null;
  note?: string;
  analysis?: string;
}

export interface IUpdateWorkCommentRequest {
  commentId: number;
  content: string;
  notifyAudience?: NotifyAudience;
  visibility?: string;
}

export interface IUpdateWorkCommentResponse {
  commentId: number;
  modifiedByUserName: string;
  modifiedAt: Date;
}

export interface ICreateWorkCommentRequest {
  workId: number;
  content?: string;
  notifyAudience?: NotifyAudience;
  visibility?: string;
}

export interface ICreateWorkCommentResponse {
  id: number;
  createdAt: Date;
  createByUserName: string;
}

export interface ICreateWorkImageRequest {
  workId: number;
  imageContent: string;
  type: string;
  note?: string;
  location?: { latitude: number; longitude: number } | null;
  caption?: string;
}

export interface ICreateWorkImageResponse {
  id: number;
}

export const WorkAvailabilityPriority = {
  Preferred: 'Preferred',
  SecondPreference: 'SecondPreference',
} as const;

export type WorkAvailabilityPriorityValue =
  (typeof WorkAvailabilityPriority)[keyof typeof WorkAvailabilityPriority];

export const WORK_AVAILABILITY_PRIORITIES = [
  {
    value: WorkAvailabilityPriority.Preferred,
    labelKey: 'priorityPreferred',
    labelFallback: 'Preferred',
  },
  {
    value: WorkAvailabilityPriority.SecondPreference,
    labelKey: 'prioritySecondPreference',
    labelFallback: 'Second Preference',
  },
] as const;

export const parseWorkAvailabilityPriority = (
  value: unknown
): WorkAvailabilityPriorityValue | undefined =>
  WORK_AVAILABILITY_PRIORITIES.find((option) => option.value === value)?.value;

export const isWorkAvailabilityPriority = (
  value: unknown
): value is WorkAvailabilityPriorityValue =>
  parseWorkAvailabilityPriority(value) !== undefined;

export const WORK_AVAILABILITY_DURATION_MAX = 720;
export const WORK_AVAILABILITY_DURATION_STEP = 15;

export const workAvailabilityDurations = Array.from(
  {
    length:
      WORK_AVAILABILITY_DURATION_MAX / WORK_AVAILABILITY_DURATION_STEP + 1,
  },
  (_, i) => i * WORK_AVAILABILITY_DURATION_STEP
);

export const formatWorkAvailabilityDuration = (minutes: number): string => {
  if (minutes === 0) return '0';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}min`;
  if (mins === 0) return hours === 1 ? '1hour' : `${hours} hours`;
  return `${hours}h ${mins}min`;
};

export interface IWorkAvailabilityWindow {
  localStartAt?: string;
  localEndAt?: string;
  orderSiteAvailabilityWindowId?: number | null;
  id: number;
  workId: number;
  startAt: string;
  endAt: string;
  timezoneId: string;
  priority: WorkAvailabilityPriorityValue;
  sourceControlled?: string | null;
  status: boolean;
  isAllDay?: boolean;
  duration?: number | null;
}

export interface ICreateWorkAvailabilityWindowRequest {
  workId: number;
  startAt: string;
  endAt: string;
  timezoneId: string;
  priority: WorkAvailabilityPriorityValue;
  sourceControlled?: string;
  status: boolean;
  isAllDay?: boolean;
  duration?: number;
}

export interface IUpdateWorkAvailabilityWindowRequest {
  workId: number;
  startAt?: string;
  endAt?: string;
  timezoneId?: string;
  priority?: WorkAvailabilityPriorityValue;
  sourceControlled?: string;
  status?: boolean;
  isAllDay?: boolean;
  duration?: number | null;
}

export type GetWorkAvailabilityWindowsParams = PagedQueryParams;
